
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { User } from '../types';

const Logo: React.FC = () => (
  <a href="/#/" className="flex items-center space-x-3">
     <svg width="40" height="40" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="text-brand-green">
      <circle cx="50" cy="50" r="50" fill="currentColor"/>
      <path d="M74 42 L94 62 L94 77 L44 77 L24 57 L24 42 L34 42 A15 8 0 0 0 64 42 L74 42 M74 57 L94 77" fill="currentColor" className="opacity-80"/>
      <path d="M24,35 A26,13 0,0,1 76,35 V57 C86,59 81,72 71,72 L29,72 C19,72 14,59 24,57 Z" fill="white"/>
      <ellipse cx="50" cy="35" rx="11" ry="5" fill="currentColor"/>
    </svg>
    <span className="text-xl font-bold text-brand-dark dark:text-slate-200">Papirrullen</span>
  </a>
);

const ShoppingCartIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c.51 0 .962-.343 1.087-.835l1.823-6.831A1.125 1.125 0 0018.143 8.25H5.438M7.5 14.25L5.106 5.165A1.125 1.125 0 004.02 4.5H3" />
    </svg>
);

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
);

const HamburgerIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
);

interface HeaderProps {
  cartItemCount: number;
  onCartClick: () => void;
  isAdminView: boolean;
  onToggleAdminView: () => void;
  isB2BView: boolean;
  onToggleB2BView: () => void;
  hasAdminAccess?: boolean;
  user: User | null;
  onLoginClick: () => void;
  onLogout: () => void;
  navigate: (path: string) => void;
}

const Header: React.FC<HeaderProps> = (props) => {
  const { cartItemCount, onCartClick, isAdminView, onToggleAdminView, isB2BView, onToggleB2BView, hasAdminAccess, user, onLoginClick, onLogout, navigate } = props;
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = e.currentTarget.getAttribute('href');
    if (!href) return;

    const targetHash = href.substring(1); // converts '/#/about' to '#/about'

    // If we're already on the page for this link, the hashchange event won't fire.
    // So, we manually scroll to the element.
    if (window.location.hash === targetHash) {
      e.preventDefault();
      const targetId = targetHash.substring(2); // converts '#/about' to 'about'
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // Otherwise, we let the default link behavior happen, which updates the hash,
    // triggers the hashchange event, and our router will handle scrolling.
  };
  
  const handleMobileNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e);
    setIsMobileMenuOpen(false);
  };
  
  const handleMobileActionClick = (action: () => void) => {
    action();
    setIsMobileMenuOpen(false);
  }

  const renderUserActions = (isMobile = false) => (
    <>
        {user ? (
          <div className={`flex items-center ${isMobile ? 'space-x-6' : 'space-x-4'}`}>
            <a 
              href="/#/account" 
              onClick={isMobile ? () => handleMobileActionClick(() => navigate('/#/account')) : undefined} 
              className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors" 
              aria-label={t('my_account_btn')}
              title={t('my_account_btn')}
            >
              <UserIcon className="w-7 h-7" />
            </a>
            <button 
              onClick={isMobile ? () => handleMobileActionClick(onLogout) : onLogout} 
              className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors"
              aria-label={t('b2b_logout_btn')}
              title={t('b2b_logout_btn')}
            >
              <LogoutIcon className="w-7 h-7" />
            </button>
          </div>
        ) : (
          <button 
            onClick={isMobile ? () => handleMobileActionClick(onLoginClick) : onLoginClick} 
            className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors" 
            aria-label={t('customer_login_btn')}
            title={t('customer_login_btn')}
          >
              <UserIcon className="w-7 h-7" />
          </button>
        )}
    </>
  );

  return (
    <>
    <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Logo />
          </div>
          
          <div className="hidden md:flex flex-1 justify-center">
            {!isAdminView && !isB2BView && (
              <nav className="flex items-center space-x-8">
                <a href="/#/about" onClick={handleNavClick} className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_about')}</a>
                <a href="/#/products" onClick={handleNavClick} className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_products')}</a>
                <a href="/#/subscription" onClick={handleNavClick} className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_subscribe')}</a>
                <a href="/#/contact" onClick={handleNavClick} className="text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_contact')}</a>
              </nav>
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {renderUserActions()}
            
            <button
              onClick={onCartClick}
              className="relative text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors"
              aria-label={t('cart_aria_label')}
            >
              <ShoppingCartIcon className="w-7 h-7" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-brand-green rounded-full">
                  {cartItemCount}
                </span>
              )}
            </button>
            
            {!isAdminView && (
              <button
                onClick={onToggleB2BView}
                className="px-4 py-2 text-sm font-medium text-brand-dark border border-brand-dark rounded-full hover:bg-brand-dark hover:text-white transition-all dark:text-slate-300 dark:border-slate-300 dark:hover:bg-slate-300 dark:hover:text-slate-900"
              >
                {isB2BView ? t('b2c_view_btn') : t('b2b_view_btn')}
              </button>
            )}

            {hasAdminAccess && (
                 <button
                  onClick={onToggleAdminView}
                  className="px-4 py-2 text-sm font-medium text-brand-green border border-brand-green rounded-full hover:bg-brand-green hover:text-white transition-all dark:text-brand-green dark:border-brand-green dark:hover:bg-brand-green dark:hover:text-slate-900"
                >
                  {isAdminView ? t('customer_view_btn') : t('admin_panel_btn')}
                </button>
            )}
          </div>
          
          {/* Mobile Actions */}
          <div className="flex items-center space-x-2 md:hidden">
             <button
              onClick={onCartClick}
              className="relative text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors"
              aria-label={t('cart_aria_label')}
            >
              <ShoppingCartIcon className="w-7 h-7" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-brand-green rounded-full">
                  {cartItemCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-1 text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors"
              aria-label="Open menu"
            >
              <HamburgerIcon className="w-7 h-7" />
            </button>
          </div>
        </div>
      </div>
    </header>
    
    {/* Mobile Menu Overlay */}
    {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white dark:bg-slate-900 z-50">
           <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between h-20 border-b border-slate-200 dark:border-slate-800">
                <Logo />
                <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors"
                    aria-label="Close menu"
                >
                    <XIcon className="w-7 h-7" />
                </button>
            </div>
            
            <div className="flex-grow overflow-y-auto">
                {!isAdminView && !isB2BView && (
                    <nav className="flex flex-col items-start space-y-6 py-8">
                        <a href="/#/about" onClick={handleMobileNavClick} className="text-2xl font-semibold text-slate-700 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_about')}</a>
                        <a href="/#/products" onClick={handleMobileNavClick} className="text-2xl font-semibold text-slate-700 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_products')}</a>
                        <a href="/#/subscription" onClick={handleMobileNavClick} className="text-2xl font-semibold text-slate-700 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_subscribe')}</a>
                        <a href="/#/contact" onClick={handleMobileNavClick} className="text-2xl font-semibold text-slate-700 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green transition-colors">{t('nav_contact')}</a>
                    </nav>
                )}
            </div>
            
            <div className="flex-shrink-0 py-6 border-t border-slate-200 dark:border-slate-800 space-y-6">
                <div className="px-2">{renderUserActions(true)}</div>
                 {!isAdminView && (
                    <button
                      onClick={() => handleMobileActionClick(onToggleB2BView)}
                      className="w-full text-left px-2 text-lg font-medium text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green"
                    >
                      {isB2BView ? t('b2c_view_btn') : t('b2b_view_btn')}
                    </button>
                 )}
                 {hasAdminAccess && (
                     <button
                      onClick={() => handleMobileActionClick(onToggleAdminView)}
                      className="w-full text-left px-2 text-lg font-medium text-slate-600 hover:text-brand-green dark:text-slate-300 dark:hover:text-brand-green"
                    >
                      {isAdminView ? t('customer_view_btn') : t('admin_panel_btn')}
                    </button>
                )}
            </div>
           </div>
        </div>
    )}
    </>
  );
};

export default Header;
